import { List, Rows3 } from "lucide-react";
import type { CareerMoveBucket } from "@career-ops-amplify/shared";
import { cn } from "@/cn";
import { BUCKET_LABELS } from "./format";
import type { MovesDensity } from "./career-move-card";

const HOME_BUCKETS: Array<CareerMoveBucket | "ALL_ACTIVE"> = [
  "ALL_ACTIVE",
  "ACT_NOW",
  "REPLY_NEEDED",
  "READY_TO_ADVANCE",
  "AT_RISK",
  "WAITING",
  "NURTURE",
];

type Props = {
  bucket: CareerMoveBucket | "ALL_ACTIVE";
  counts?: Record<string, number>;
  onChange: (bucket: CareerMoveBucket | "ALL_ACTIVE") => void;
  density?: MovesDensity;
  onDensityChange?: (density: MovesDensity) => void;
};

export function CareerMoveBucketSelector({
  bucket,
  counts,
  onChange,
  density,
  onDensityChange,
}: Props) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-1.5">
      {HOME_BUCKETS.map((b) => {
        const count = counts?.[b] ?? 0;
        const active = bucket === b;
        return (
          <button
            key={b}
            type="button"
            onClick={() => onChange(b)}
            className={cn(
              "rounded-md border px-2.5 py-1 text-xs font-medium transition",
              active
                ? "border-brand/50 bg-brand/10 text-brand"
                : "border-border text-muted-foreground hover:bg-muted/20",
            )}
          >
            {BUCKET_LABELS[b] ?? b}
            {b !== "ALL_ACTIVE" ? (
              <span className="ml-1 opacity-70">{count}</span>
            ) : null}
          </button>
        );
      })}
      {density && onDensityChange ? (
        <div
          className="ml-auto inline-flex items-center gap-0.5 rounded-md border border-border p-0.5"
          role="group"
          aria-label="List density"
        >
          <button
            type="button"
            title="Compact"
            aria-label="Compact list"
            aria-pressed={density === "compact"}
            onClick={() => onDensityChange("compact")}
            className={cn(
              "inline-flex size-7 items-center justify-center rounded transition",
              density === "compact"
                ? "bg-brand/10 text-brand"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <List className="size-3.5" />
          </button>
          <button
            type="button"
            title="Roomy"
            aria-label="Roomy list"
            aria-pressed={density === "roomy"}
            onClick={() => onDensityChange("roomy")}
            className={cn(
              "inline-flex size-7 items-center justify-center rounded transition",
              density === "roomy"
                ? "bg-brand/10 text-brand"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Rows3 className="size-3.5" />
          </button>
        </div>
      ) : null}
    </div>
  );
}
